/**
 * Bootstrap Translation Catalog
 * 
 * Minimal English strings bundled with the shell to ensure the UI never
 * shows a blank screen during initial load or if the BFF is unavailable.
 * 
 * IMPORTANT: Keep this minimal! Only include:
 * - Loading states
 * - Fatal error messages
 * - Critical navigation (home link in error states)
 * - Session-related messages
 * 
 * Full translations are loaded from BFF at runtime.
 * 
 * @module i18n/bootstrap
 * @see docs/designs/LOCALIZATION_NORTH_STAR.md
 */

import type { TranslationCatalog } from '../types/i18n';

/**
 * Bootstrap catalog - always available, even offline
 * These strings ensure the app remains functional during:
 * - Initial load before BFF responds
 * - Network failures
 * - BFF errors
 */
export const BOOTSTRAP_CATALOG: TranslationCatalog = {
  // ===========================================================================
  // Loading States
  // ===========================================================================
  'shell.loading': 'Loading...',
  'shell.loading.app': 'Loading application...',
  'shell.loading.plugin': 'Loading {pluginName}...',
  'shell.loading.content': 'Loading content...',
  
  // ===========================================================================
  // Error Messages
  // ===========================================================================
  'shell.error.generic': 'Something went wrong',
  'shell.error.genericWithRetry': 'Something went wrong. Please try again.',
  'shell.error.network': 'Network error. Please check your connection.',
  'shell.error.timeout': 'Request timed out. Please try again.',
  'shell.error.serverError': 'Server error. Please try again later.',
  'shell.error.notFound': 'Page not found',
  'shell.error.notFoundDescription': 'The page you are looking for does not exist or has been moved.',
  'shell.error.accessDenied': 'Access denied',
  'shell.error.accessDeniedDescription': 'You do not have permission to access this resource.',
  'shell.error.unauthorized': 'Unauthorized',
  'shell.error.unauthorizedDescription': 'Please log in to continue.',
  'shell.error.pluginLoadFailed': 'Failed to load plugin',
  'shell.error.pluginLoadFailedDescription': 'The plugin could not be loaded. Please try again or contact support.',
  'shell.error.pluginDisabled': 'Plugin disabled',
  'shell.error.pluginDisabledTitle': '{pluginName} is Currently Disabled',
  'shell.error.pluginDisabledDescription': 'This app has been temporarily disabled by your administrator.',
  'shell.error.pluginDisabledContactHint': 'If you need access, please contact your administrator to request it be enabled.',
  
  // ===========================================================================
  // Session & Authentication
  // ===========================================================================
  'shell.session.expired': 'Session expired',
  'shell.session.expiredDescription': 'Your session has expired. Please log in again.',
  'shell.session.expiring': 'Session expiring soon',
  'shell.session.expiringDescription': 'Your session will expire in {minutes} minutes. Save your work.',
  'shell.session.idle': 'Session idle',
  'shell.session.idleDescription': 'You have been inactive. Click to continue.',
  
  // ===========================================================================
  // Actions (common buttons)
  // ===========================================================================
  'shell.actions.retry': 'Retry',
  'shell.actions.goHome': 'Go Home',
  'shell.actions.goBack': 'Go Back',
  'shell.actions.login': 'Log In',
  'shell.actions.logout': 'Log Out',
  'shell.actions.continue': 'Continue',
  'shell.actions.cancel': 'Cancel',
  'shell.actions.close': 'Close',
  'shell.actions.save': 'Save',
  'shell.actions.submit': 'Submit',
  'shell.actions.confirm': 'Confirm',
  'shell.actions.delete': 'Delete',
  'shell.actions.edit': 'Edit',
  'shell.actions.create': 'Create',
  'shell.actions.refresh': 'Refresh',
  
  // ===========================================================================
  // Navigation (minimal)
  // ===========================================================================
  'shell.nav.home': 'Home',
  'shell.nav.dashboard': 'Dashboard',
  'shell.nav.back': 'Back',
  
  // ===========================================================================
  // Locale
  // ===========================================================================
  'shell.locale.label': 'Language',
  'shell.locale.changed': 'Language changed to {locale}',
  'shell.locale.loading': 'Loading translations...',
  
  // ===========================================================================
  // Accessibility
  // ===========================================================================
  'shell.a11y.loading': 'Content is loading',
  'shell.a11y.loaded': 'Content loaded',
  'shell.a11y.error': 'An error occurred',
  'shell.a11y.skipToContent': 'Skip to main content',
  'shell.a11y.mainNavigation': 'Main navigation',
  'shell.a11y.closeMenu': 'Close menu',
  'shell.a11y.openMenu': 'Open menu',
};

/**
 * Get a bootstrap translation
 * Used internally by LocalizationContext before full catalog loads
 * 
 * @param key Translation key
 * @param params Optional interpolation parameters (simple replacement only)
 * @returns Translated string or key if not found
 */
export function getBootstrapTranslation(key: string, params?: Record<string, unknown>): string {
  let text = BOOTSTRAP_CATALOG[key];
  
  if (!text) {
    return key;
  }
  
  // Simple parameter replacement (no ICU in bootstrap - keep it lightweight)
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    });
  }
  
  return text;
}

/**
 * Check if a key exists in bootstrap catalog
 */
export function hasBootstrapTranslation(key: string): boolean {
  return key in BOOTSTRAP_CATALOG;
}

export default BOOTSTRAP_CATALOG;
